import { motion } from 'framer-motion';
import { Download, Plus, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../../contexts/OverlayContext';
import { typography } from '../../../styles/typography';
import { Card } from '../../ui';
import { customerApi } from './customerApi';

/**
 * Maps API customer response to UI format
 */
const mapCustomerToUI = (customer) => {
  const billingAddress = customer.billing_address || {};
  const metadata = customer.metadata || {};
  
  return {
    id: customer.customer_id || customer.id,
    customerId: customer.customer_id,
    customerName: customer.customer_name || customer.customerName || 'Unknown',
    email: customer.contact_email || customer.email || '',
    address: billingAddress.street || customer.address || '',
    city: billingAddress.city || customer.city || '',
    state: billingAddress.state || customer.state || '',
    country: billingAddress.country || customer.country || 'India',
    pinCode: billingAddress.pincode || customer.pinCode || '',
    panNumber: customer.pan_number || customer.panNumber || '',
    gstNumber: customer.gst_number || customer.gstNumber || '',
    bankName: metadata.bank_name || customer.bankName || '',
    accountNo: metadata.account_no || customer.accountNo || '',
    ifscNo: metadata.ifsc_no || customer.ifscNo || '',
    sites: customer.site_ids || (customer.site_id ? [customer.site_id] : []) || customer.sites || [],
    siteName: customer.site_name || '',
    contactPerson: customer.contact_person || customer.contactPerson || '',
    contactPhone: customer.contact_phone || customer.contactPhone || '',
    contactMobile: customer.contact_mobile || customer.contactMobile || '',
    paymentTerms: customer.payment_terms || customer.paymentTerms || '',
    status: customer.status || 'active',
    createdAt: customer.created_at || customer.createdAt || new Date().toISOString(),
    // Full customer object for detail view
    fullCustomer: customer,
  };
};

const CustomerListView = () => {
  const navigate = useNavigate();
  const { openOverlay } = useOverlay();
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Fetch customers on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchCustomers = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        const result = await customerApi.listCustomers({
          limit: 50,
          status: 'active', // Only fetch active customers by default
        });

        if (result.success) {
          const mappedCustomers = (result.customers || []).map(mapCustomerToUI);
          if (isMounted) {
            setCustomers(mappedCustomers);
            setNextToken(result.next_token);
            setHasMore(!!result.next_token);
          }
        } else {
          if (isMounted) {
            setError(result.message || 'Failed to fetch customers');
            setCustomers([]);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch customers');
          setCustomers([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCustomers();

    return () => {
      isMounted = false;
    };
  }, []);

  // Load more customers (pagination)
  const loadMore = async () => {
    if (!hasMore || isLoading || !nextToken) return;

    setIsLoading(true);
    try {
      const result = await customerApi.listCustomers({
        limit: 50,
        status: 'active',
        last_evaluated_key: nextToken,
      });

      if (result.success) {
        const mappedCustomers = (result.customers || []).map(mapCustomerToUI);
        setCustomers(prev => [...prev, ...mappedCustomers]);
        setNextToken(result.next_token);
        setHasMore(!!result.next_token);
      }
    } catch (err) {
      setError(err.message || 'Failed to load more customers');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch full customer details when card is clicked
  const handleCardClick = async (customer) => {
    let customerToShow = customer;

    // If we don't have full customer data, fetch it
    if (!customer.fullCustomer && customer.customerId) {
      try {
        const result = await customerApi.getCustomer(customer.customerId);
        if (result.success && result.customer) {
          customerToShow = mapCustomerToUI(result.customer);
        }
      } catch (err) {
        console.error('Error fetching customer details:', err);
        // Fallback to basic customer data
      }
    }

    // Open overlay with customer data
    openOverlay('customerDetails', {
      customer: customerToShow,
      onDownload: handleDownload
    });
  };

  // Filter customers by search term
  const filteredCustomers = useMemo(() => {
    if (!searchTerm.trim()) {
      return customers;
    }
    const term = searchTerm.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.customerName.toLowerCase().includes(term) ||
        customer.email.toLowerCase().includes(term) ||
        customer.city.toLowerCase().includes(term) ||
        customer.panNumber.toLowerCase().includes(term) ||
        customer.gstNumber.toLowerCase().includes(term) ||
        customer.contactPerson.toLowerCase().includes(term)
    );
  }, [customers, searchTerm]);

  const handleDownload = (customer) => {
    const csvData = [
      ['Customer Name', 'Email', 'Address', 'City', 'State', 'PAN', 'GST', 'Bank', 'Account No', 'IFSC', 'Sites', 'Created At'],
      [
        customer.customerName,
        customer.email,
        customer.address,
        customer.city,
        customer.state,
        customer.panNumber,
        customer.gstNumber,
        customer.bankName,
        customer.accountNo,
        customer.ifscNo,
        customer.sites.join('; '),
        customer.createdAt,
      ],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-${customer.customerName}-${customer.createdAt}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={typography.h2} style={{ color: '#000000' }}>
            Customers
          </h1>
          <p className="mt-2 text-gray-600">
            Manage and view all onboarded customers
          </p>
        </div>
        <button
          onClick={() => navigate('/onboarding-customer-onboarding/create')}
          className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
        >
          <Plus className="w-4 h-4" />
          Create Customer
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers by name, email, city, PAN, or GST..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <Card.Light className="p-4 mb-4 border-red-200 bg-red-50">
          <p className="text-red-600 text-sm">{error}</p>
        </Card.Light>
      )}

      {/* Customers List */}
      <div className="space-y-4">
        {isLoading && customers.length === 0 ? (
          <Card.Light className="p-8 text-center">
            <p className="text-gray-500 text-lg">Loading customers...</p>
          </Card.Light>
        ) : filteredCustomers.length > 0 ? (
          filteredCustomers.map((customer, index) => (
            <motion.div
              key={customer.id}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <Card.Light
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => handleCardClick(customer)}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                        {index + 1}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {customer.customerName}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {customer.email}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Location: </span>
                        <span className="font-medium">
                          {customer.city}, {customer.state}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">PAN: </span>
                        <span className="font-medium">{customer.panNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">GST: </span>
                        <span className="font-medium">{customer.gstNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Sites: </span>
                        <span className="font-medium">
                          {Array.isArray(customer.sites) ? customer.sites.length : 0} site{Array.isArray(customer.sites) && customer.sites.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Bank: </span>
                        <span className="font-medium">{customer.bankName}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Account: </span>
                        <span className="font-medium">{customer.accountNo}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">IFSC: </span>
                        <span className="font-medium">{customer.ifscNo}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Created: </span>
                        <span className="font-medium">
                          {new Date(customer.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(customer);
                      }}
                      className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                      title="Download"
                    >
                      <Download className="w-6 h-6 text-white stroke-white" strokeWidth={2} />
                    </button>
                  </div> */}
                </div>
              </Card.Light>
            </motion.div>
          ))
        ) : (
          <Card.Light className="p-8 text-center">
            <p className="text-gray-500 text-lg">
              {error 
                ? error
                : searchTerm.trim()
                  ? 'No customers found matching your search criteria.'
                  : 'No customers found. Create your first customer to get started.'}
            </p>
          </Card.Light>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && (
          <div className="flex justify-center pt-4">
            <button
              onClick={loadMore}
              className="px-6 py-2 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors"
            >
              Load More
            </button>
          </div>
        )}

        {/* Loading More Indicator */}
        {isLoading && customers.length > 0 && (
          <div className="text-center py-4">
            <p className="text-gray-500 text-sm">Loading more customers...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerListView;


